import { parseStringTemplateGenerator, evaluateParsedString, PipeFunction } from 'string-template-parser';

/**
 * Пример использавания парсера
 * "tariff-period": "{period, plural, =1 {{period} месяц} =2 {{period} месяца} =3 {{period} месяца} other {{period} месяцев}}"
 *
 * */

const QUOTED_STRING_TEST = (remainingString: string) => remainingString.charAt(0) === '{';
const QUOTED_STRING_GET_AND_ADVANCE = (remainingString: string, advance: (length: number) => void) => {
  let currentPosition = 1;
  let depth = 1;
  while (depth > 0 && currentPosition < remainingString.length) {
    if (remainingString[currentPosition] === '\\') {
      currentPosition += 2;
      continue;
    }
    if (remainingString[currentPosition] === '{') {
      depth++;
    }
    if (remainingString[currentPosition] === '}') {
      depth--;
    }
    currentPosition++;
  }
  const result = remainingString.substr(0, currentPosition);
  advance(currentPosition);
  return result;
};

const parseI18NString = parseStringTemplateGenerator({
  VARIABLE_START: /^{\s*/,
  VARIABLE_END: /^\s*}/,
  PIPE_START: /^\s*,\s*/,
  PIPE_PARAMETER_START: /^\s*,\s*/,
  QUOTED_STRING_IN_PARAMETER_TEST: QUOTED_STRING_TEST,
  QUOTED_STRING_IN_PARAMETER_GET_AND_ADVANCE: QUOTED_STRING_GET_AND_ADVANCE,
});
const parseI18NParameter = parseStringTemplateGenerator({
  VARIABLE_START: /^{/,
  VARIABLE_END: /^}/,
  PIPE_START: /^$/,
  PIPE_PARAMETER_START: /^$/,
  QUOTED_STRING_TEST,
  QUOTED_STRING_GET_AND_ADVANCE,
  QUOTED_STRING_IN_PARAMETER_TEST: QUOTED_STRING_TEST,
  QUOTED_STRING_IN_PARAMETER_GET_AND_ADVANCE: QUOTED_STRING_GET_AND_ADVANCE,
});

const I18N_PIPES = {
  plural: (value: any, options: string[], values: string[]) => {
    options = options.map((option) => option.trim()).slice(0, options.length - 1);
    const mapper: { [option: string]: string } = options.reduce(
      (map, option, index) => ({ ...map, [option.charAt(0) === '=' ? option.slice(1).trim() : option.trim()]: values[index] }),
      {}
    );
    const numValue = +value;
    if (mapper.hasOwnProperty(numValue.toString())) {
      return mapper[value];
    }
    if (numValue === numValue) {
      const lastDigit: { [digit: number]: string[] } = {
        0: ['zero'],
        1: ['one'],
        2: ['two', 'few'],
        3: ['three', 'few'],
        4: ['four', 'few'],
        5: ['five', 'many'],
        6: ['six', 'many'],
        7: ['seven', 'many'],
        8: ['eight', 'many'],
        9: ['nine', 'many'],
      };
      return mapper.hasOwnProperty(value)
        ? mapper[value]
        : [
            ...(numValue >= 10 && numValue < 20 ? ['many'] : lastDigit[numValue % 10] || ['many']),
            ...(numValue >= 20 ? ['many'] : []),
            'other',
          ]
            .filter((key) => mapper.hasOwnProperty(key))
            .map((key) => mapper[key])[0] || '';
    }
    return mapper['other'] == null ? '' : mapper['other'];
  },
  select: (value: any, options: string[], values: string[]) => {
    options = options.map((option) => option.trim()).slice(0, options.length - 1);
    const mapper: { [option: string]: string } = options.reduce((map, option, index) => ({ ...map, [option.trim()]: values[index] }), {});
    return mapper[value] == null ? '' : mapper[value];
  },
};

export type ICUPipeFunction = (value: any, options: string[], parameters: string[]) => string;

export class TranslateICUCustomParser {
  private pipeEntries: [string, ICUPipeFunction][] = [];

  constructor(private additionalPipes?: { [pipeName: string]: ICUPipeFunction }) {
    this.pipeEntries = Object.entries({ ...(additionalPipes || {}), ...I18N_PIPES });
  }

  private getPipes(variables: { [varName: string]: string }): { [pipeName: string]: PipeFunction } {
    return this.pipeEntries
      .map(([pipeName, pipe]) => ({
        [pipeName]: (value: any, [parameter]: string[]) => {
          const parsedParameter = parseI18NParameter(parameter);
          const options = parsedParameter.literals;
          const values = parsedParameter.variables.map((variable) => this.interpolate(variable.name, variables));

          return pipe(value, options, values);
        },
      }))
      .reduce((acc, crt) => ({ ...acc, ...crt }), {});
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  interpolate(expr: string | Function, params?: any): string {
    return typeof expr === 'function'
      ? expr(params)
      : evaluateParsedString(parseI18NString(expr), params, this.getPipes(params), (variableName: string) =>
          this.getValue(params, variableName)
        );
  }

  getValue(target: any, key: string): any {
    const keys = (key && key.split('.')) || [];
    let keyResult = '';
    do {
      keyResult += keys.shift();
      if (target != null && target[keyResult] != null && (typeof target[keyResult] === 'object' || !keys.length)) {
        target = target[keyResult];
        keyResult = '';
      } else if (!keys.length) {
        target = undefined;
      } else {
        keyResult += '.';
      }
    } while (keys.length);

    return isDefined(target) ? target : key;
  }
}

export class TranslateICUParser extends TranslateICUCustomParser {
  constructor() {
    super({});
  }
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function isDefined(value: any): boolean {
  return typeof value !== 'undefined' && value !== null;
}
